import { useRef, useState } from "react";
import { SyncProgress } from "./SyncProgress";
import { SearchHints } from "./SearchHints";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Search, Inbox as InboxIcon, RefreshCw, Star, Paperclip, Menu, Archive, X, Loader2 } from "lucide-react";
import { usePullToRefresh, useSwipe } from "@/lib/touch";
import { toast } from "sonner";

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
    const yr = d.getFullYear() === now.getFullYear() ? undefined : "numeric";
    return d.toLocaleDateString([], { month: "short", day: "numeric", year: yr });
  } catch {
    return "";
  }
}

function displayName(sender) {
  if (!sender) return "Unknown";
  if (sender.name && sender.name.trim()) {
    const parts = sender.name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[1] ? parts[1][0] + "." : ""}`.trim();
  }
  if (sender.email) return sender.email.split("@")[0];
  return "Unknown";
}

function MobileRow({ t, active, onSelect, statusColor, statusLabel, onArchive, onStar }) {
  const sender = t.participants?.find(
    (p) => p.email && !p.email.includes("@internalinbox.com")
  ) || t.participants?.[0];
  const senderLabel = displayName(sender);
  const swipe = useSwipe({
    onSwipeLeft: () => onStar?.(t),
    onSwipeRight: () => onArchive?.(t),
    threshold: 80,
  });
  return (
    <div
      onClick={() => onSelect(t.id)}
      {...swipe}
      role="button"
      className={`flex items-start gap-3 px-4 py-3 border-b border-zinc-100 cursor-pointer transition-colors min-h-[64px] ${active ? "bg-[#C2DBFF]" : t.unread ? "bg-white" : "bg-[#F8FAFD]"}`}
      data-testid={`thread-row-${t.id}`}
    >
      <span
        className="w-2 h-2 rounded-full shrink-0 mt-1.5"
        style={{ background: statusColor(t.status) }}
        title={statusLabel(t.status)}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`flex-1 min-w-0 truncate text-[15px] ${t.unread ? "font-bold text-zinc-900" : "text-zinc-700"}`}>
            {senderLabel}
            {t.message_count > 1 && (
              <span className="ml-1 text-xs text-zinc-500 font-normal">{t.message_count}</span>
            )}
          </span>
          <span className={`shrink-0 text-xs tabular-nums ${t.unread ? "text-zinc-900 font-semibold" : "text-zinc-500"}`}>
            {timeAgo(t.last_message_at)}
          </span>
        </div>
        <div className={`text-[14px] truncate ${t.unread ? "font-semibold text-zinc-900" : "text-zinc-800"}`}>
          {t.subject || "(no subject)"}
        </div>
        <div className="text-[13px] text-zinc-500 truncate mt-0.5 flex items-center gap-1.5">
          {t.has_attachments && <Paperclip className="w-3 h-3 shrink-0" strokeWidth={1.75} />}
          <span className="truncate">{t.snippet}</span>
        </div>
      </div>
    </div>
  );
}

function DesktopRow({ t, active, onSelect, statusColor, statusLabel, compact }) {
  const sender = t.participants?.find(
    (p) => p.email && !p.email.includes("@internalinbox.com")
  ) || t.participants?.[0];
  const senderLabel = displayName(sender);
  const unread = t.unread;
  return (
    <div
      onClick={() => onSelect(t.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(t.id); } }}
      className={`thread-row group hidden md:flex items-center gap-2 pl-2 pr-3 border-b border-zinc-100 cursor-pointer transition-colors ${
        active ? "bg-[#C2DBFF]" : unread ? "bg-white hover:shadow-[inset_1px_0_0_#dadce0,inset_-1px_0_0_#dadce0,0_1px_2px_0_rgba(60,64,67,0.30),0_1px_3px_1px_rgba(60,64,67,0.15)] hover:z-10 hover:relative" : "bg-[#F2F6FC] hover:shadow-[inset_1px_0_0_#dadce0,inset_-1px_0_0_#dadce0,0_1px_2px_0_rgba(60,64,67,0.30),0_1px_3px_1px_rgba(60,64,67,0.15)] hover:z-10 hover:relative"
      }`}
      style={{ height: "32px" }}
      data-testid={`thread-row-${t.id}`}
    >
      <span className="w-4 h-4 shrink-0 border border-zinc-400 rounded-sm opacity-60 group-hover:opacity-100" />
      <Star className="w-4 h-4 shrink-0 text-zinc-400 hover:text-amber-400" strokeWidth={1.5} />
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: statusColor(t.status) }} title={statusLabel(t.status)} />
      <span className={`shrink-0 text-sm truncate ${compact ? "w-32" : "w-44"} ${unread ? "font-bold text-zinc-900" : "text-zinc-700"}`} title={senderLabel}>
        {senderLabel}
        {t.message_count > 1 && (
          <span className="ml-1 text-xs text-zinc-500">{t.message_count}</span>
        )}
      </span>
      <span className="flex-1 min-w-0 text-sm truncate">
        <span className={unread ? "font-bold text-zinc-900" : "text-zinc-800"}>{t.subject || "(no subject)"}</span>
        {t.snippet && <span className="text-zinc-500"> - {t.snippet}</span>}
      </span>
      {t.has_attachments && <Paperclip className="w-3.5 h-3.5 shrink-0 text-zinc-400" strokeWidth={1.5} />}
      <span className={`shrink-0 text-xs text-right tabular-nums w-24 whitespace-nowrap ${unread ? "text-zinc-900 font-bold" : "text-zinc-500"}`}>
        {timeAgo(t.last_message_at)}
      </span>
    </div>
  );
}

export default function ThreadList({
  threads, loading, selectedId, onSelect, search, setSearch, onSearchSubmit, activeQuery, onClearSearch, total, statuses,
  onRefresh, refreshing, lastSyncedAt, compact, onOpenSidebar,
  status, setStatus, onLocalChange, syncInboxes,
}) {
  const searchRef = useRef(null);
  const searching = loading && !!activeQuery;
  const [hintsOpen, setHintsOpen] = useState(false);
  const statusMap = {};
  (statuses || []).forEach((s) => { statusMap[s.id] = s; });
  const statusColor = (id) => statusMap[id]?.color || "transparent";
  const statusLabel = (id) => statusMap[id]?.label || id;

  const handleRefresh = async () => { await onRefresh?.(); };
  const { containerRef, indicatorOffset, isRefreshing } = usePullToRefresh(handleRefresh);

  const archiveThread = async (t) => {
    const closed = (statuses || []).find((s) => s.id === "closed");
    if (!closed) return;
    try {
      await api.put(`/threads/${t.id}/status`, { status: closed.id });
      toast.success("Marked closed");
      onLocalChange?.();
    } catch { toast.error("Couldn't update"); }
  };

  const starThread = (t) => {
    // Quick visual: toast for now; future = persist a "starred" tag
    toast.success(t.starred ? "Star removed" : "Starred");
  };

  return (
    <div
      className={`${compact ? "md:w-[420px] md:shrink-0 w-full" : "flex-1"} border-r border-zinc-200 flex flex-col h-screen bg-white min-w-0 pb-14 md:pb-0`}
      data-testid="thread-list-pane"
    >
      {/* Search */}
      <div className="h-14 border-b border-zinc-200 px-3 flex items-center gap-2">
        {onOpenSidebar && (
          <button
            onClick={onOpenSidebar}
            className="md:hidden shrink-0 w-10 h-10 rounded-full hover:bg-zinc-100 active:bg-zinc-200 flex items-center justify-center text-zinc-600"
            title="Menu"
            data-testid="thread-list-menu-button"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" strokeWidth={1.75} />
          </button>
        )}
        <form
          className="relative flex-1 max-w-2xl flex items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); setHintsOpen(false); onSearchSubmit?.(); }}
          data-testid="thread-search-form"
        >
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" strokeWidth={1.75} />
            <Input
              ref={searchRef}
              placeholder={typeof window !== "undefined" && window.innerWidth < 640 ? "Search mail" : "Search mail — try from: or has:attachment"}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setHintsOpen(true); }}
              onFocus={() => setHintsOpen(true)}
              onClick={() => setHintsOpen(true)}
              onBlur={() => setHintsOpen(false)}
              onKeyDown={(e) => { if (e.key === "Escape") { setHintsOpen(false); e.currentTarget.blur(); } }}
              className="h-10 pl-9 rounded-full bg-zinc-100 border-transparent text-sm focus-visible:ring-1 focus-visible:ring-[#0033CC] focus-visible:bg-white"
              data-testid="thread-search-input"
              inputMode="search"
              enterKeyHint="search"
              type="search"
            />
            {hintsOpen && !search.trim() && (
              <SearchHints
                onPick={(op) => {
                  if (op.endsWith(":")) { setSearch(op); requestAnimationFrame(() => searchRef.current?.focus()); }
                  else { setHintsOpen(false); onSearchSubmit?.(op.trim()); }
                }}
              />
            )}
          </div>
          <button
            type="submit"
            disabled={!search.trim() || searching}
            className="shrink-0 h-10 md:h-9 px-4 rounded-full bg-[#0033CC] text-white text-sm font-medium hover:bg-[#0029A3] active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed transition-[background-color,transform,opacity] duration-150 flex items-center gap-1.5"
            data-testid="thread-search-submit-button"
          >
            {searching && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.25} />}
            {searching ? "Searching" : "Search"}
          </button>
        </form>
      </div>

      {/* Sticky stage chips — mobile only */}
      {statuses && statuses.length > 0 && (
        <div className="md:hidden overflow-x-auto whitespace-nowrap border-b border-zinc-100 px-3 py-2 flex gap-2 scrollbar-none" data-testid="mobile-stage-chips">
          <button
            onClick={() => setStatus?.(null)}
            className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium transition-colors ${!status ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`}
            data-testid="stage-chip-all"
          >
            All
          </button>
          {statuses.map((s) => {
            const active = status === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setStatus?.(active ? null : s.id)}
                className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${active ? "text-white" : "bg-zinc-100 text-zinc-700"}`}
                style={active ? { background: s.color } : {}}
                data-testid={`stage-chip-${s.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? "white" : s.color }} />
                {s.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Count bar */}
      <div className="px-4 h-8 text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500 border-b border-zinc-100 flex items-center gap-2" data-testid="thread-count-label">
        <InboxIcon className="w-3 h-3" strokeWidth={2} />
        {loading ? "Loading…" : `${total} conversation${total === 1 ? "" : "s"}`}
        {activeQuery && (
          <span
            className="normal-case tracking-normal font-medium text-[11px] text-[#0033CC] bg-[#0033CC]/[0.07] rounded-full pl-2.5 pr-1 py-0.5 flex items-center gap-1 min-w-0"
            data-testid="active-search-chip"
          >
            <span className="truncate max-w-[220px]">{searching ? `Searching for “${activeQuery}”…` : `Results for “${activeQuery}”`}</span>
            <button
              type="button"
              onClick={onClearSearch}
              className="w-4 h-4 rounded-full hover:bg-[#0033CC]/15 flex items-center justify-center"
              title="Clear search"
              data-testid="active-search-clear-button"
            >
              <X className="w-3 h-3" strokeWidth={2.25} />
            </button>
          </span>
        )}
        {lastSyncedAt && !loading && (
          <span className="ml-auto normal-case tracking-normal text-[10px] text-zinc-400" data-testid="last-synced">
            synced {timeAgo(lastSyncedAt)}
          </span>
        )}
      </div>

      <SyncProgress inboxes={syncInboxes} />

      {/* List */}
      <div
        ref={containerRef}
        className={`flex-1 overflow-y-auto scroll-smooth-mobile relative transition-opacity duration-200 ${searching ? "opacity-40 pointer-events-none" : ""}`}
        data-testid="thread-list"
        style={{ WebkitOverflowScrolling: "touch", overscrollBehaviorY: "contain" }}
      >
        {/* Pull-to-refresh indicator */}
        {(indicatorOffset > 0 || isRefreshing) && (
          <div
            className="md:hidden absolute left-0 right-0 top-0 flex items-center justify-center pointer-events-none"
            style={{ height: indicatorOffset || 48, transition: isRefreshing ? "height 200ms" : "none" }}
            data-testid="pull-refresh-indicator"
          >
            <RefreshCw
              className={`w-5 h-5 text-[#0033CC] ${isRefreshing ? "animate-spin" : ""}`}
              strokeWidth={1.75}
              style={{ transform: !isRefreshing ? `rotate(${(indicatorOffset / 70) * 360}deg)` : undefined }}
            />
          </div>
        )}

        <div style={{ transform: indicatorOffset > 0 ? `translateY(${indicatorOffset}px)` : undefined, transition: isRefreshing ? "transform 200ms" : "none" }}>
          {!loading && threads.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500" data-testid="thread-list-empty">
              {activeQuery ? `No conversations match “${activeQuery}”.` : "No conversations match your filters."}
            </div>
          )}
          {threads.map((t) => {
            const active = selectedId === t.id;
            return (
              <div key={t.id}>
                {/* Mobile two-line row */}
                <div className="md:hidden">
                  <MobileRow
                    t={t}
                    active={active}
                    onSelect={onSelect}
                    statusColor={statusColor}
                    statusLabel={statusLabel}
                    onArchive={archiveThread}
                    onStar={starThread}
                  />
                </div>
                {/* Desktop dense row */}
                <DesktopRow
                  t={t}
                  active={active}
                  onSelect={onSelect}
                  statusColor={statusColor}
                  statusLabel={statusLabel}
                  compact={compact}
                />
              </div>
            );
          })}
          <div className="md:hidden h-6" />
          {/* Swipe hint footer */}
          {threads.length > 0 && (
            <div className="md:hidden px-4 py-3 text-[11px] text-zinc-400 text-center flex items-center justify-center gap-3 border-t border-zinc-100">
              <span className="inline-flex items-center gap-1"><Archive className="w-3 h-3" strokeWidth={1.75} /> swipe right to close</span>
              <span className="text-zinc-300">·</span>
              <span className="inline-flex items-center gap-1"><Star className="w-3 h-3" strokeWidth={1.75} /> swipe left to star</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
